import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Clock, Users, Target } from "lucide-react";
import { useTranslation } from "react-i18next";

const TEAM_IMG = "https://images.unsplash.com/photo-1759272548449-7b689a81c8fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxsb2dpc3RpY3MlMjB3b3JrZXIlMjB0YWJsZXQlMjBzaGlwcGluZyUyMHBvcnR8ZW58MHx8fHwxNzczNDMzMDE1fDA&ixlib=rb-4.1.0&q=85";

export default function AboutPage() {
  const { t } = useTranslation();
  const values = [
    { icon: Shield, title: t("about.value_reliability_title"), desc: t("about.value_reliability_desc") },
    { icon: Clock, title: t("about.value_punctuality_title"), desc: t("about.value_punctuality_desc") },
    { icon: Users, title: t("about.value_team_title"), desc: t("about.value_team_desc") },
    { icon: Target, title: t("about.value_custom_title"), desc: t("about.value_custom_desc") },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="bg-[#09090B] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-4 font-heading">
              {t("about.hero_tagline")}
            </p>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight uppercase leading-[0.9]">
              {t("about.hero_title_l1")}
              <br />
              <span className="text-[#FF5200]">{t("about.hero_title_l2")}</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-4 font-heading">
                {t("about.story_tagline")}
              </p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight text-[#09090B] mb-6">
                {t("about.story_title")}
              </h2>
              <div className="space-y-4 text-zinc-600 text-lg leading-relaxed font-body">
                <p>{t("about.story_p1")}</p>
                <p>{t("about.story_p2")}</p>
                <p>{t("about.story_p3")}</p>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
              <div className="relative overflow-hidden border-2 border-zinc-200">
                <img src={TEAM_IMG} alt={t("about.story_tagline")} className="w-full h-80 md:h-[480px] object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-[#FAFAFA] border-y-2 border-zinc-200" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="mb-14">
            <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-2 font-heading">
              {t("about.values_tagline")}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-[#09090B]">
              {t("about.values_title")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((val, i) => (
              <motion.div key={val.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.4 }}
                className="border-2 border-zinc-200 bg-white p-8 hover:border-[#09090B] transition-colors duration-300" data-testid={`value-card-${i}`}>
                <val.icon size={40} className="text-[#FF5200] mb-6" strokeWidth={1.5} />
                <h3 className="font-heading text-xl font-bold uppercase mb-3 text-[#09090B]">{val.title}</h3>
                <p className="text-zinc-500 leading-relaxed font-body">{val.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#09090B]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight uppercase mb-6">
            {t("about.cta_title")}
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-10 font-body">
            {t("about.cta_desc")}
          </p>
          <Link to="/preventivo" data-testid="about-cta"
            className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm">
            {t("about.cta_btn")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
}
